import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Column, ColumnDef, Row } from '@tanstack/react-table';
import { Chip, cn, Popover, Tooltip } from '@c2fo/liquidity';
import colorConfig from '@c2fo/liquidity/colors';
import { ArrowDownIcon, ArrowUpIcon, InfoCircleIcon, ShapesDuotoneIcon } from '@c2fo/liquidity/icons';
import BuyerLogo from '@/enterprise/components/BuyerLogo';
import ClosedMarketChip from '@/enterprise/components/ClosedMarketChip';
import EmptyRender from '@/enterprise/components/EmptyRender';
import {
  getNewDivisionAlertEligibleTakerMarkets,
  useEnableNewDivisionAlert,
} from '@/enterprise/components/NewDivisionAlert';
import NextMarketClearTimeCountdown from '@/enterprise/components/NextMarketClearTimeCountdown';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import MarketActionsMenu from '@/enterprise/features/homepage/marketTable/MarketActionsMenu';
import RateDisplay from '@/enterprise/features/homepage/marketTable/RateDisplay';
import { buildCustomScheduleSentenceFromExistingOfferSchedule } from '@/enterprise/features/offerModal/utils';
import ParticipationSwitch from '@/enterprise/features/participationSwitch/ParticipationSwitch';
import RenewalRequestIcon from '@/enterprise/features/preferred/RenewalRequestIcon';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import getRenewalSchedule from '@/utils/getRenewalSchedule';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useIsNewDivision from '@/utils/useIsNewDivision';
import useLocaleFormat from '@/utils/useLocaleFormat';
import usePricingTypeDisplay from '@/utils/usePricingTypeDisplay';
const RightAlignedCell = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex items-center justify-end">{children}</div>;
};

const TableHeaderCell = ({
  children,
  column,
  rightAligned,
}: {
  children: React.ReactNode;
  column: Column<TakerMarket, unknown>;
  rightAligned?: boolean;
}) => {
  const arrowIconProps = {
    fill: column.getIsSorted() ? colorConfig.secondary[500] : 'transparent',
  };
  const ArrowIcon = (
    <div>
      {column.getIsSorted() === 'desc' ? <ArrowDownIcon {...arrowIconProps} /> : <ArrowUpIcon {...arrowIconProps} />}
    </div>
  );

  return (
    <div className="flex items-center gap-1">
      {rightAligned && column.getCanSort() && <span>{ArrowIcon}</span>}
      <span>{children}</span>
      {!rightAligned && column.getCanSort() && <span>{ArrowIcon}</span>}
    </div>
  );
};

interface DivisionCellConfig {
  showBuyerInfo: boolean;
  includeNextMarketClearTimeCountdown: boolean;
}

const DivisionCell = ({
  takerMarket,
  isFirstMarketWithRecurringRules,
  isFirstMarketWithSchedule,
  config,
}: {
  takerMarket: TakerMarket;
  isFirstMarketWithRecurringRules: boolean;
  isFirstMarketWithSchedule: boolean;
  config: DivisionCellConfig;
}) => {
  const { t } = useTranslation();
  const { asDateStringText } = useLocaleFormat();
  const { isNewDivision } = useIsNewDivision();
  const enableNewDivisionAlert = useEnableNewDivisionAlert();
  const getNewDivisionAlertEligible = enableNewDivisionAlert ? getNewDivisionAlertEligibleTakerMarkets : undefined;
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const { getPricingTypeDisplay } = usePricingTypeDisplay();
  const divisionCellData = useDivisionCellData(
    takerMarket,
    isNewDivision,
    getRenewalSchedule,
    getRecurringRulesForTakerMarkets,
    getNewDivisionAlertEligible
  );

  return (
    <IntercomDataTarget
      target="firstMarketTableDivisionWithSchedule"
      targetCondition={isFirstMarketWithSchedule}
      className="flex max-w-56 gap-2 truncate"
    >
      {config.showBuyerInfo && (
        <BuyerLogo
          buyerLogoUrl={takerMarket.makerOrganizationLogo}
          buyerName={takerMarket.makerOrganizationName}
          size="md"
        />
      )}
      <div className="max-w-56 space-y-1 truncate">
        {config.showBuyerInfo && (
          <div className="truncate" title={takerMarket.makerOrganizationName}>
            {takerMarket.makerOrganizationName}
          </div>
        )}
        <div className={cn('space-y-0.5 truncate ', { 'space-y-1': !config.showBuyerInfo })}>
          <div className="flex items-center gap-1 truncate">
            {divisionCellData.hasRecurringRules && (
              <IntercomDataTarget
                target="firstAutomationIcon"
                targetCondition={isFirstMarketWithRecurringRules}
                className="inline-block"
              >
                <ShapesDuotoneIcon className="h-4 w-4 fill-blue-500" data-testid="recurring-rules-icon" />
              </IntercomDataTarget>
            )}

            {divisionCellData.newDivision && (
              <Chip label={t('core.new')} isRound size="xs" type="info" variant="filled" />
            )}
            <Link
              className={cn('relative w-56 truncate text-base font-bold text-blue-500', {
                'text-sm': config.showBuyerInfo,
              })}
              to={asEnterpriseLink(
                `customers/${takerMarket.makerOrganizationUuid}/markets/${takerMarket.marketId}/division/${takerMarket.takerDivisionId}/invoices/eligible`
              )}
              title={getTakerMarketName(takerMarket)}
            >
              {getTakerMarketName(takerMarket)}
            </Link>
          </div>
          <div className="truncate text-sm" title={getTakerMarketDivisionTitle(takerMarket)?.title}>
            {getTakerMarketDivisionTitle(takerMarket)?.content}
          </div>
          {!takerMarket.marketIsEnabled ? (
            <ClosedMarketChip />
          ) : (
            !config.showBuyerInfo && (
              <div className="text-sm text-text-secondary">{getPricingTypeDisplay(takerMarket)}</div>
            )
          )}
          <div className="flex items-center gap-1 text-sm">
            <div className="flex items-center gap-1">
              <div>{takerMarket.currency}</div>
              {config.includeNextMarketClearTimeCountdown && (
                <>
                  <div className="h-[3px] w-[3px] shrink-0 rounded-full bg-text-primary" />
                  <NextMarketClearTimeCountdown asIcon takerMarket={takerMarket} />
                </>
              )}
            </div>
          </div>
        </div>
        {divisionCellData.hasOfferSchedule && (
          <div className="flex items-center gap-1 text-xs text-text-secondary ">
            <span>{t('scheduling.scheduledOffer')}</span>
            <Popover>
              <Popover.Trigger>
                <InfoCircleIcon className="h-[13px] w-[13px] fill-gray-700" />
              </Popover.Trigger>
              <Popover.Content arrow className="max-w-[240px] text-center">
                {buildCustomScheduleSentenceFromExistingOfferSchedule({
                  offerSchedule: takerMarket.offerSchedule!,
                  t,
                })}
              </Popover.Content>
            </Popover>
          </div>
        )}
        {divisionCellData.hasRenewalSchedule && takerMarket.disableAfterMarketClearsDate && (
          <div className="flex items-center gap-1 text-xs text-text-secondary ">
            <span>Automatic Renewal</span>
            <Popover>
              <Popover.Trigger>
                <InfoCircleIcon className="h-[13px] w-[13px] fill-gray-700" />
              </Popover.Trigger>
              <Popover.Content arrow className="max-w-[240px] text-center" collisionPadding={24}>
                <Trans
                  i18nKey="offer.autoRenew.modal.disable.info"
                  values={{
                    renewalIntervalDays: divisionCellData.renewalIntervalDays,
                    disableAfterMarketClearsDate: asDateStringText({
                      date: takerMarket.disableAfterMarketClearsDate,
                      isUTC: false,
                      formatOptions: { month: 'long' },
                    }),
                  }}
                  components={{ bold: <strong /> }}
                />
              </Popover.Content>
            </Popover>
          </div>
        )}
        {takerMarket.offerConfig.offerType !== 'PREFERRED_TERM' ? (
          <>
            {divisionCellData.hasFrequency && (
              <div className="flex items-center gap-1 text-xs text-text-secondary ">
                <span>
                  {divisionCellData.isOneTimeOffer
                    ? takerMarket.offerConfig.offerType === 'EXPRESS_ACCEPT'
                      ? t('builder.expressAcceptOffer')
                      : t('builder.oneTimeOffer')
                    : t('builder.recurringOffer')}
                </span>
                <Popover>
                  <Popover.Trigger>
                    <InfoCircleIcon className="h-[13px] w-[13px] fill-gray-700" />
                  </Popover.Trigger>
                  <Popover.Content arrow className="max-w-48 text-center">
                    {divisionCellData.isOneTimeOffer
                      ? takerMarket.offerConfig.offerType === 'EXPRESS_ACCEPT'
                        ? t('core.oneTimeExpressAcceptInfo')
                        : t('builder.oneTimeInfo')
                      : t('builder.recurringInfo')}
                  </Popover.Content>
                </Popover>
              </div>
            )}
            {divisionCellData.hasExpirationDate && (
              <div className="flex items-center gap-1 text-xs text-text-secondary ">
                <span>{t('taker.dictionary.expirationDate.label')}</span>
                <Popover>
                  <Popover.Trigger>
                    <InfoCircleIcon className="h-[13px] w-[13px] fill-gray-700" />
                  </Popover.Trigger>
                  <Popover.Content arrow className="max-w-[240px] text-center">
                    {takerMarket.disableAfterMarketClearsDate &&
                      asDateStringText({ date: takerMarket.disableAfterMarketClearsDate, isUTC: false })}
                  </Popover.Content>
                </Popover>
              </div>
            )}
          </>
        ) : null}
      </div>
    </IntercomDataTarget>
  );
};

export const getDivisionName = (market: TakerMarket): string => {
  return market.marketType ? `${market.makerDivisionName}-${market.legacyMarketId}` : market.makerDivisionName;
};

const sortingHelperFn = (
  rowA: Row<TakerMarket>,
  rowB: Row<TakerMarket>,
  columnId: string,
  getNewDivisionAlertEligible?: (takerMarkets: TakerMarket[]) => TakerMarket[]
) => {
  const marketA = rowA.original;
  const marketB = rowB.original;

  let divisionNameA: string | undefined;
  let divisionNameB: string | undefined;

  const getDivisionNames = () => {
    if (divisionNameA === undefined) {
      divisionNameA = getDivisionName(marketA);
      divisionNameB = getDivisionName(marketB);
    }

    return { divisionNameA, divisionNameB };
  };

  // First, check if either market is a new division
  if (getNewDivisionAlertEligible) {
    const aIsNew = getNewDivisionAlertEligible([marketA]).includes(marketA);
    const bIsNew = getNewDivisionAlertEligible([marketB]).includes(marketB);

    // If one is new and the other isn't, the new one always comes first
    if (aIsNew && !bIsNew) {
      return 1;
    }

    if (!aIsNew && bIsNew) {
      return -1;
    }
    // If both are new or both are not new, fall through to the regular sorting below
  }

  switch (columnId) {
    case 'totalInvoiceAmount': {
      const diff = marketA.totalInvoiceAmountUsd - marketB.totalInvoiceAmountUsd;

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'eligibleInvoiceAmount': {
      const diff =
        marketA.eligibleInvoiceAmountUsd +
        marketA.awardedInvoiceAmountUsd -
        (marketB.eligibleInvoiceAmountUsd + marketB.awardedInvoiceAmountUsd);

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'participation': {
      const sortKeyA = marketA.offerConfig?.isEnabled ? 1 : -1;
      const sortKeyB = marketB.offerConfig?.isEnabled ? 1 : -1;
      const diff = sortKeyA - sortKeyB;

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'makerOrganizationName': {
      const { divisionNameA, divisionNameB } = getDivisionNames();
      const nameCompare = divisionNameA!.localeCompare(divisionNameB!);

      if (nameCompare !== 0) {
        return nameCompare;
      }

      return marketB.eligibleInvoiceAmountUsd - marketA.eligibleInvoiceAmountUsd;
    }

    case 'eligibleInvoiceCount': {
      const diff =
        marketA.eligibleInvoiceCount +
        marketA.awardedInvoiceCount -
        (marketB.eligibleInvoiceCount + marketB.awardedInvoiceCount);

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'disableAfterMarketClearsDate': {
      const sortKeyA = marketA.disableAfterMarketClearsDate || '1900-01-01';
      const sortKeyB = marketB.disableAfterMarketClearsDate || '1900-01-01';
      const dateCompare = sortKeyA.localeCompare(sortKeyB);

      if (dateCompare !== 0) {
        return dateCompare;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'takerExcludedInvoiceAmount': {
      const diff = marketA.takerExcludedInvoiceAmount - marketB.takerExcludedInvoiceAmount;

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'offerConfig': {
      const sortKeyA =
        marketA.offerConfig.isDiscountBidding && marketA.offerConfig.maxDiscount
          ? marketA.offerConfig.maxDiscount
          : marketA.offerConfig.maxApr ?? -1;
      const sortKeyB =
        marketB.offerConfig.isDiscountBidding && marketB.offerConfig.maxDiscount
          ? marketB.offerConfig.maxDiscount
          : marketB.offerConfig.maxApr ?? -1;
      const diff = sortKeyA - sortKeyB;

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'clearingInvoiceAmount': {
      const amountA = marketA.clearingInvoiceAmount - marketA.clearingEarn;
      const amountB = marketB.clearingInvoiceAmount - marketB.clearingEarn;
      const diff = amountA - amountB;

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'marketPayDate': {
      const sortKeyA = marketA.marketPayDate || '1900-01-01';
      const sortKeyB = marketB.marketPayDate || '1900-01-01';
      const dateCompare = sortKeyA.localeCompare(sortKeyB);

      if (dateCompare !== 0) {
        return dateCompare;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'marketGoodFundsDate': {
      const sortKeyA = marketA.marketGoodFundsDate || '1900-01-01';
      const sortKeyB = marketB.marketGoodFundsDate || '1900-01-01';
      const dateCompare = sortKeyA.localeCompare(sortKeyB);

      if (dateCompare !== 0) {
        return dateCompare;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    case 'eligibleDpeWeightedAvg': {
      const diff = marketA.eligibleDpeWeightedAvg - marketB.eligibleDpeWeightedAvg;

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }

    default: {
      const diff = marketA.totalInvoiceAmountUsd - marketB.totalInvoiceAmountUsd;

      if (diff !== 0) {
        return diff;
      }

      const { divisionNameA, divisionNameB } = getDivisionNames();
      return divisionNameB!.localeCompare(divisionNameA!);
    }
  }
};

const useDivisionCellData = (
  takerMarket: TakerMarket,
  isNewDivision: (date?: string) => boolean,
  getRenewalSchedule: (takerMarket: TakerMarket) => { hasRenewalSchedule: boolean; renewalIntervalDays?: number },
  getRecurringRulesForTakerMarkets: (markets: TakerMarket[]) => unknown[],
  getNewDivisionAlertEligible?: (takerMarkets: TakerMarket[]) => TakerMarket[]
) => {
  return useMemo(() => {
    const newDivision =
      isNewDivision(takerMarket.userAddedToDivision) ||
      getNewDivisionAlertEligible?.([takerMarket]).includes(takerMarket);
    const hasRecurringRules = getRecurringRulesForTakerMarkets([takerMarket]).length > 0;
    const { hasRenewalSchedule, renewalIntervalDays } = getRenewalSchedule(takerMarket);
    const isOneTimeOffer = takerMarket.offerConfig.frequency === 'ONE_TIME';
    const hasExpirationDate = !!takerMarket.disableAfterMarketClearsDate && !isOneTimeOffer;
    const hasOfferSchedule = !!takerMarket.offerSchedule && takerMarket.offerSchedule.type !== 'renewal';
    const hasFrequency =
      takerMarket.offerConfig.offerType !== 'PREFERRED_TERM' && !hasOfferSchedule && takerMarket.offerConfig.isEnabled;

    return {
      newDivision,
      hasRecurringRules,
      hasRenewalSchedule,
      renewalIntervalDays: renewalIntervalDays ?? 0,
      hasExpirationDate,
      hasOfferSchedule,
      hasFrequency,
      isOneTimeOffer,
    };
  }, [takerMarket, isNewDivision, getRenewalSchedule, getRecurringRulesForTakerMarkets, getNewDivisionAlertEligible]);
};

const useColumns = ({
  firstMarketWithRecurringRulesIndex,
  showBuyerInfo,
  firstMarketWithScheduleIndex,
  includeNextMarketClearTimeCountdown,
}: {
  firstMarketWithRecurringRulesIndex: number;
  showBuyerInfo: boolean;
  firstMarketWithScheduleIndex: number;
  includeNextMarketClearTimeCountdown: boolean;
}) => {
  const { t } = useTranslation();
  const { listenToMarketStats } = useMarketStatsStatus();
  const { asCurrency, asDateStringText, asNumber } = useLocaleFormat();
  const enableNewDivisionAlert = useEnableNewDivisionAlert();
  const getNewDivisionAlertEligible = enableNewDivisionAlert ? getNewDivisionAlertEligibleTakerMarkets : undefined;

  const divisionCellConfig: DivisionCellConfig = useMemo(
    () => ({
      showBuyerInfo,
      includeNextMarketClearTimeCountdown,
    }),
    [showBuyerInfo, includeNextMarketClearTimeCountdown]
  );

  const sortingFunctions = useMemo(
    () => ({
      participation: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'participation', getNewDivisionAlertEligible),
      makerOrganizationName: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'makerOrganizationName', getNewDivisionAlertEligible),
      totalInvoiceAmount: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'totalInvoiceAmount', getNewDivisionAlertEligible),
      eligibleInvoiceAmount: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'eligibleInvoiceAmount', getNewDivisionAlertEligible),
      eligibleInvoiceCount: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'eligibleInvoiceCount', getNewDivisionAlertEligible),
      disableAfterMarketClearsDate: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'disableAfterMarketClearsDate', getNewDivisionAlertEligible),
      takerExcludedInvoiceAmount: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'takerExcludedInvoiceAmount', getNewDivisionAlertEligible),
      offerConfig: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'offerConfig', getNewDivisionAlertEligible),
      clearingInvoiceAmount: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'clearingInvoiceAmount', getNewDivisionAlertEligible),
      marketPayDate: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'marketPayDate', getNewDivisionAlertEligible),
      marketGoodFundsDate: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'marketGoodFundsDate', getNewDivisionAlertEligible),
      eligibleDpeWeightedAvg: (rowA: Row<TakerMarket>, rowB: Row<TakerMarket>) =>
        sortingHelperFn(rowA, rowB, 'eligibleDpeWeightedAvg', getNewDivisionAlertEligible),
    }),
    [getNewDivisionAlertEligible]
  );

  const headerCells = useMemo(
    () => ({
      participation: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <TableHeaderCell column={column}>{t('core.participation')}</TableHeaderCell>
      ),
      division: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <TableHeaderCell column={column}>{t('core.division')}</TableHeaderCell>
      ),
      totalAmount: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('core.totalAmount')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
      included: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('core.included')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
      invoices: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('maker.invoices')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
      termEnd: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('taker.termEnd')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
      excluded: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('core.excluded')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
      rate: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('core.rate')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
      estimatedAmount: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('core.estimatedAmount')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
      paymentInitiation: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('core.paymentInitiation')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
      estimatedDepositDate: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <div className="flex items-center gap-1">
            <TableHeaderCell column={column} rightAligned>
              {t('draftOffer.labels.estimatedDepositDate')}
            </TableHeaderCell>
            <Tooltip>
              <Tooltip.Trigger aria-label={t('core.nextEstimatedDepositDate')}>
                <InfoCircleIcon className="fill-blue-500" />
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  arrow
                  className="max-w-60 whitespace-pre-wrap text-center"
                  collisionPadding={24}
                  side="bottom"
                  variant="dark"
                >
                  {t('core.nextEstimatedDepositDateTooltip')}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip>
          </div>
        </RightAlignedCell>
      ),
      daysPaidEarly: ({ column }: { column: Column<TakerMarket, unknown> }) => (
        <RightAlignedCell>
          <TableHeaderCell column={column} rightAligned>
            {t('core.daysPaidEarly')}
          </TableHeaderCell>
        </RightAlignedCell>
      ),
    }),
    [t]
  );

  const columns: ColumnDef<TakerMarket>[] = useMemo(
    () => [
      {
        accessorKey: 'participation',
        header: headerCells.participation,
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <ParticipationSwitch takerMarket={row.original} />
          </div>
        ),
        size: 164,
        sortingFn: sortingFunctions.participation,
        sortDescFirst: true,
      },
      {
        accessorKey: 'makerOrganizationName',
        header: headerCells.division,
        cell: ({ row }) => (
          <DivisionCell
            takerMarket={row.original}
            isFirstMarketWithRecurringRules={row.index === firstMarketWithRecurringRulesIndex}
            isFirstMarketWithSchedule={row.index === firstMarketWithScheduleIndex}
            config={divisionCellConfig}
          />
        ),
        size: 224,
        sortingFn: sortingFunctions.makerOrganizationName,
        sortDescFirst: true,
      },
      {
        accessorKey: 'totalInvoiceAmount',
        header: headerCells.totalAmount,
        cell: ({ row }) => (
          <RightAlignedCell>{asCurrency(row.original.totalInvoiceAmount, row.original.currency)}</RightAlignedCell>
        ),
        sortingFn: sortingFunctions.totalInvoiceAmount,
        sortDescFirst: true,
      },
      {
        accessorKey: 'eligibleInvoiceAmount',
        header: headerCells.included,
        cell: ({ row }) => {
          const listeningForMarketStat = listenToMarketStats([
            {
              marketUuid: row.original.marketUuid,
              takerId: row.original.takerDivisionId,
              takerDivisionUuid: row.original.takerDivisionUuid,
              makerOrganizationUuid: row.original.makerOrganizationUuid,
            },
          ]);
          return (
            <RightAlignedCell>
              <span className={cn({ 'blur-sm': listeningForMarketStat })}>
                {asCurrency(
                  row.original.eligibleInvoiceAmount + row.original.awardedInvoiceAmount,
                  row.original.currency
                )}
              </span>
            </RightAlignedCell>
          );
        },
        sortingFn: sortingFunctions.eligibleInvoiceAmount,
        sortDescFirst: true,
      },
      {
        accessorKey: 'eligibleInvoiceCount',
        header: headerCells.invoices,
        cell: ({ row }) => {
          const listeningForMarketStat = listenToMarketStats([
            {
              marketUuid: row.original.marketUuid,
              takerId: row.original.takerDivisionId,
              takerDivisionUuid: row.original.takerDivisionUuid,
              makerOrganizationUuid: row.original.makerOrganizationUuid,
            },
          ]);
          return (
            <RightAlignedCell>
              <span className={cn({ 'blur-sm': listeningForMarketStat })}>
                {asNumber({
                  value: row.original.eligibleInvoiceCount + row.original.awardedInvoiceCount,
                })}
                /
                {asNumber({
                  value:
                    row.original.eligibleInvoiceCount +
                    row.original.takerExcludedInvoiceCount +
                    row.original.awardedInvoiceCount,
                })}
              </span>
            </RightAlignedCell>
          );
        },
        sortingFn: sortingFunctions.eligibleInvoiceCount,
        sortDescFirst: true,
      },
      {
        accessorKey: 'disableAfterMarketClearsDate',
        header: headerCells.termEnd,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <EmptyRender condition={!!row.original.disableAfterMarketClearsDate}>
              {row.original.disableAfterMarketClearsDate &&
                asDateStringText({ date: row.original.disableAfterMarketClearsDate, isUTC: false })}
            </EmptyRender>
            <RenewalRequestIcon takerMarket={row.original} iconStyles="h-5 w-5 fill-red-600" />
          </div>
        ),
        sortingFn: sortingFunctions.disableAfterMarketClearsDate,
        sortDescFirst: true,
        sortUndefined: false,
      },
      {
        accessorKey: 'takerExcludedInvoiceAmount',
        header: headerCells.excluded,
        cell: ({ row }) => {
          const listeningForMarketStat = listenToMarketStats([
            {
              marketUuid: row.original.marketUuid,
              takerId: row.original.takerDivisionId,
              takerDivisionUuid: row.original.takerDivisionUuid,
              makerOrganizationUuid: row.original.makerOrganizationUuid,
            },
          ]);
          return (
            <RightAlignedCell>
              <span className={cn('font-medium', { 'blur-sm': listeningForMarketStat })}>
                {asCurrency(row.original.takerExcludedInvoiceAmount, row.original.currency)}
              </span>
            </RightAlignedCell>
          );
        },
        sortingFn: sortingFunctions.takerExcludedInvoiceAmount,
        sortDescFirst: true,
      },
      {
        accessorKey: 'offerConfig',
        header: headerCells.rate,
        cell: ({ row }) => {
          const listeningForMarketStat = listenToMarketStats([
            {
              marketUuid: row.original.marketUuid,
              takerId: row.original.takerDivisionId,
              takerDivisionUuid: row.original.takerDivisionUuid,
              makerOrganizationUuid: row.original.makerOrganizationUuid,
            },
          ]);
          return (
            <RightAlignedCell>
              <div className={cn('text-right', { 'blur-sm': listeningForMarketStat })}>
                <RateDisplay takerMarket={row.original} />
              </div>
            </RightAlignedCell>
          );
        },
        sortingFn: sortingFunctions.offerConfig,
        sortDescFirst: true,
      },
      {
        accessorKey: 'clearingInvoiceAmount',
        header: headerCells.estimatedAmount,
        cell: ({ row }) => {
          const listeningForMarketStat = listenToMarketStats([
            {
              marketUuid: row.original.marketUuid,
              takerId: row.original.takerDivisionId,
              takerDivisionUuid: row.original.takerDivisionUuid,
              makerOrganizationUuid: row.original.makerOrganizationUuid,
            },
          ]);
          const estimatedAmount = row.original.clearingInvoiceAmount - row.original.clearingEarn;
          return (
            <RightAlignedCell>
              <span className={cn({ 'blur-sm': listeningForMarketStat })}>
                {asCurrency(estimatedAmount, row.original.currency)}
              </span>
            </RightAlignedCell>
          );
        },
        sortingFn: sortingFunctions.clearingInvoiceAmount,
        sortDescFirst: true,
      },
      {
        accessorKey: 'marketPayDate',
        header: headerCells.paymentInitiation,
        cell: ({ row }) => {
          const listeningForMarketStat = listenToMarketStats([
            {
              marketUuid: row.original.marketUuid,
              takerId: row.original.takerDivisionId,
              takerDivisionUuid: row.original.takerDivisionUuid,
              makerOrganizationUuid: row.original.makerOrganizationUuid,
            },
          ]);
          return (
            <RightAlignedCell>
              <span className={cn({ 'blur-sm': listeningForMarketStat })}>
                <EmptyRender condition={row.original.marketIsEnabled && !!row.original.marketPayDate}>
                  {row.original.marketPayDate && asDateStringText({ date: row.original.marketPayDate })}
                </EmptyRender>
              </span>
            </RightAlignedCell>
          );
        },
        sortingFn: sortingFunctions.marketPayDate,
        sortDescFirst: true,
        sortUndefined: false,
      },
      {
        accessorKey: 'marketGoodFundsDate',
        header: headerCells.estimatedDepositDate,
        cell: ({ row }) => {
          const listeningForMarketStat = listenToMarketStats([
            {
              marketUuid: row.original.marketUuid,
              takerId: row.original.takerDivisionId,
              takerDivisionUuid: row.original.takerDivisionUuid,
              makerOrganizationUuid: row.original.makerOrganizationUuid,
            },
          ]);
          return (
            <RightAlignedCell>
              <span className={cn({ 'blur-sm': listeningForMarketStat })}>
                <EmptyRender condition={row.original.marketIsEnabled && !!row.original.marketGoodFundsDate}>
                  {row.original.marketGoodFundsDate && asDateStringText({ date: row.original.marketGoodFundsDate })}
                </EmptyRender>
              </span>
            </RightAlignedCell>
          );
        },
        sortingFn: sortingFunctions.marketGoodFundsDate,
        sortDescFirst: true,
        sortUndefined: false,
      },
      {
        accessorKey: 'eligibleDpeWeightedAvg',
        header: headerCells.daysPaidEarly,
        cell: ({ row }) => {
          const listeningForMarketStat = listenToMarketStats([
            {
              marketUuid: row.original.marketUuid,
              takerId: row.original.takerDivisionId,
              takerDivisionUuid: row.original.takerDivisionUuid,
              makerOrganizationUuid: row.original.makerOrganizationUuid,
            },
          ]);
          const totalDpeWeightedSum = row.original.eligibleDpeWeightedSum + row.original.awardedDpeWeightedSum;
          const totalInvoiceAmount = row.original.eligibleInvoiceAmount + row.original.awardedInvoiceAmount;
          const dpeAverage = totalInvoiceAmount > 0 ? totalDpeWeightedSum / totalInvoiceAmount : 0;

          return (
            <RightAlignedCell>
              <span className={cn({ 'blur-sm': listeningForMarketStat })}>
                <EmptyRender condition={totalInvoiceAmount > 0 && dpeAverage > 0}>
                  {t('core.daysWithCount', {
                    count: dpeAverage,
                    formattedCount: asNumber({
                      value: dpeAverage,
                      decimals: 0,
                    }),
                  })}
                </EmptyRender>
              </span>
            </RightAlignedCell>
          );
        },
        sortingFn: sortingFunctions.eligibleDpeWeightedAvg,
        sortDescFirst: true,
      },
      {
        id: 'actions',
        cell: ({ row }) => {
          const isFirstMarketWithSchedule = row.index === firstMarketWithScheduleIndex;
          return (
            <IntercomDataTarget
              target={isFirstMarketWithSchedule ? 'firstMarketActionsMenuWithSchedule' : 'marketActionsMenu'}
              className="inline-block"
            >
              <MarketActionsMenu takerMarket={row.original} />
            </IntercomDataTarget>
          );
        },
        size: 72,
        enableSorting: false,
      },
    ],
    [
      headerCells,
      sortingFunctions,
      divisionCellConfig,
      firstMarketWithRecurringRulesIndex,
      firstMarketWithScheduleIndex,
      listenToMarketStats,
      t,
      asDateStringText,
      asCurrency,
      asNumber,
    ]
  );

  return columns;
};

export default useColumns;
